/**
 * Live match sessions (operators only, /ops/live, apps/api/src/routes/live.ts): a match uploaded as
 * segments, each segment's pipeline step, review and rebuild, its clips with their overlays, the
 * session's players, a player's clips and export.
 */
import { get, post } from "./client";
import type { PipelineJob, PipelineProgress, ProgressCounter, VideoState } from "./types";

export interface LiveSessionBrief {
  id: string;
  title: string;
  state: "open" | "closed";
  pitch_length_choice: "full" | "junior" | "unknown";
  camera_setup_id: string | null;
  calibration_video_id: string | null;
  created_at: string;
  updated_at: string;
  segments?: number;
  segments_ready?: number;
  duration_ms?: number;
  calibration?: { camera_setup_id: string; video_id: string | null; segment_seq: number | null; frame_url: string | null } | null;
}

export interface LiveMetric { value: number | string | null; status: "ok" | "unavailable"; unit: string | null; reason: string | null }
export type LiveMetrics = Partial<Record<"bowl_speed" | "bat_swing_speed" | "ball_impact_speed" | "ball_speed_estimate" | "bounce_length_m" | "bounce_line_m" | "length_class" | "line_class", LiveMetric>>;
export interface LivePlayerRef { key: string; label: string; role: "batsman" | "bowler" }

export interface LiveClip {
  id: string;
  candidate_id: string;
  index: number;
  window_ms: { start: number; end: number } | null;
  fitted_ms?: { start: number; end: number } | null;
  events_ms: { release?: number | null; bounce?: number | null; impact?: number | null };
  impact_type: string | null;
  metrics: LiveMetrics;
  playback_url: string | null;
  bowler: LivePlayerRef | null;
  batsman: LivePlayerRef | null;
  segment?: { seq: number; video_id: string; staged: boolean; overlays: string | null };
}

export interface LiveSegment {
  seq: number;
  video: { id: string; title: string; state: VideoState; duration_ms: number | null; fps: number | null; image_size: [number, number] | null; error: { code: string; message: string | null } | null; created_at: string; updated_at: string };
  pipeline: { job: PipelineJob | null; attempt: { n: number; state: string } | null; progress: PipelineProgress | null };
  calibration: { method: string; camera_setup_id: string; drift_px: number | null; session_setup: boolean } | null;
  review: { state: "queued" | "staged" | "failed" | null; review_id: string | null; error: string | null; url: string | null; job: PipelineJob | null };
  rebuild: { job: PipelineJob | null; rebuilt_at: string | null; summary: LiveRebuildSummary | null };
  overlays: string | null;
  run_id: string | null;
  rejected: string[];
  clips: LiveClip[];
}

export interface LiveRebuildSummary {
  n_deliveries?: number;
  n_retracked?: number;
  n_marked?: number;
  n_changed?: number;
  candidates_changed?: number;
  seconds?: number;
  changed?: { delivery_id: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null; clicks?: number; marked?: boolean }[];
}

export interface LivePlayer { key: string; id: string; role: "batsman" | "bowler"; order: number; name: string | null; label: string; clip_count: number; crop_url: string | null }

export interface LiveSessionDetail {
  session: LiveSessionBrief;
  segments: LiveSegment[];
  players: { status: "ok" | "unavailable" | null; reason: string | null; busy: boolean; segments: string[]; batsmen: LivePlayer[]; bowlers: LivePlayer[] };
  label_review_url: string | null;
}

export interface LivePlayerDetail {
  session: LiveSessionBrief;
  player: LivePlayer;
  clips: LiveClip[];
  exports: { id: string; state: string; options: Record<string, unknown>; created_at: string }[];
}

/** One clip's overlay (gemini-cv-project src/live/overlays.py): points [frame, x, y, src] in working-copy pixels. */
export type TracePoint = [number, number, number, "click" | "track" | "filled"];
export interface LiveOverlay {
  delivery_id: string;
  frames: { release: number | null; bounce: number | null; bat: number | null; release_drawn?: number };
  impact_type: string | null;
  contact: boolean;
  events_from: "marks" | "m09";
  rejected: boolean;
  speeds: { bowl_kmh: number | null; bat_kmh: number | null; impact_kmh: number | null; unavailable?: Record<string, string> | null };
  zone: { full_toss: boolean; code: string | null; length_m?: number | null; line_m?: number | null; length_class?: string | null; line_class?: string | null } | null;
  path: TracePoint[];
  hit: TracePoint[];
  bounce_px: [number, number] | null;
  bat_px: [number, number] | null;
  end: number | null;
  counts: { click?: number; track?: number; filled?: number };
}
export interface LiveOverlays { version: string; frame: { width: number; height: number; fps: number }; deliveries: Record<string, LiveOverlay> }

export interface LiveExportView { export: { id: string; state: "requested" | "ready" | "failed"; options: Record<string, unknown>; created_at: string; expires_at: string | null }; job: { id: string; state: string; error: { code: string; message: string | null } | null } | null; download_url: string | null }
export interface LiveExportOptions { speeds: boolean; traces: boolean; pre_s: number; post_s: number; map_s: number }

export type { ProgressCounter };

export const live = {
  sessions: () => get<{ items: LiveSessionBrief[] }>("/ops/live/sessions"),
  create: (title: string, pitch_length_choice: "full" | "junior" | "unknown") => post<{ session: LiveSessionBrief }>("/ops/live/sessions", { title, pitch_length_choice }),
  session: (id: string) => get<LiveSessionDetail>(`/ops/live/sessions/${id}`),
  overlays: (url: string) => get<LiveOverlays>(url),
  rebuild: (videoId: string) => post<{ job_id: string; created: boolean }>(`/ops/live/segments/${videoId}/rebuild`),
  player: (sessionId: string, key: string) => get<LivePlayerDetail>(`/ops/live/sessions/${sessionId}/players/${key}`),
  rename: (sessionId: string, key: string, name: string | null) => post<{ key: string; name: string | null }>(`/ops/live/sessions/${sessionId}/players/${key}/name`, { name }),
  exportPlayer: (sessionId: string, key: string, o: LiveExportOptions) => post<{ export_id: string; job_id: string }>(`/ops/live/sessions/${sessionId}/players/${key}/export`, o),
  exportView: (id: string, fileName: string) => get<LiveExportView>(`/ops/live/exports/${id}?file_name=${encodeURIComponent(fileName)}`),
};

/** The segment's stage on the user-facing rail: Uploaded, Being set up, Finding every ball, Clips ready. */
export function liveStage(state: VideoState): 0 | 1 | 2 | 3 | -1 {
  if (state === "uploading" || state === "uploaded" || state === "ingesting") return 0;
  if (state === "calibration_queue" || state === "calibrating") return 1;
  if (state === "calibrated" || state === "queued" || state === "processing" || state === "held") return 2;
  if (state === "ready") return 3;
  return -1;
}

/** A metric's number, rounded, or null. */
export function metricNum(m: LiveMetric | undefined, digits = 0): number | null {
  if (!m || m.status !== "ok" || typeof m.value !== "number" || !Number.isFinite(m.value)) return null;
  const k = 10 ** digits;
  return Math.round(m.value * k) / k;
}

const LINE_LETTER: Record<string, string> = { off: "O", middle: "S", leg: "L" };
/** The pitch zone code (length letter + line letter, YO..SL) from the clip's published classes. */
export function zoneCode(m: LiveMetrics): string | null {
  const len = m.length_class?.status === "ok" ? String(m.length_class.value ?? "") : "";
  const line = m.line_class?.status === "ok" ? String(m.line_class.value ?? "") : "";
  if (!len || !line) return null;
  return len.slice(0, 1).toUpperCase() + (LINE_LETTER[line] ?? line.slice(0, 1).toUpperCase());
}
